'use strict';

/** Maximum age before a persisted risk score violates the 24h freshness SLA. */
const RISK_SCORE_FRESHNESS_SLA_MS = 24 * 60 * 60 * 1000;

const ACTIVE_PROCESSING = new Set(['queued', 'processing', 'retrying']);

/**
 * @param {object|null|undefined} employee
 * @returns {number|null}
 */
function riskScoreLastUpdatedMs(employee) {
  const raw =
    employee?.risk_score_last_computed_at ??
    employee?.risk_score_payload?.metadata?.evaluatedAt ??
    employee?.risk_score_payload?.metadata?.generatedAt ??
    null;
  const ts = new Date(raw || '').getTime();
  return Number.isFinite(ts) ? ts : null;
}

/**
 * @param {object|null|undefined} employee
 * @param {number} [nowMs]
 */
function isRiskScoreSnapshotStale(employee, nowMs = Date.now()) {
  if (!employee || typeof employee !== 'object') return true;
  const score = Number(employee.risk_score);
  const payload = employee.risk_score_payload;
  const scoreReady =
    payload && typeof payload === 'object' && payload.scoreReady !== false && Number.isFinite(score);
  if (!scoreReady && !Number.isFinite(score)) return true;

  const updatedMs = riskScoreLastUpdatedMs(employee);
  if (updatedMs == null) return true;
  const ageMs = nowMs - updatedMs;
  return ageMs < 0 || ageMs >= RISK_SCORE_FRESHNESS_SLA_MS;
}

function isRiskActivelyProcessingEmployee(employee) {
  const state = String(employee?.risk_score_payload?.metadata?.processingState || '').toLowerCase();
  return ACTIVE_PROCESSING.has(state);
}

/**
 * Canonical persisted status for SLA enforcement.
 * @param {object|null|undefined} employee
 * @param {number} [nowMs]
 * @returns {'completed'|'processing'|'failed'|'stale'}
 */
function deriveRiskScoreStatus(employee, nowMs = Date.now()) {
  if (!employee || typeof employee !== 'object') return 'stale';

  const processingState = String(employee?.risk_score_payload?.metadata?.processingState || '').toLowerCase();
  if (ACTIVE_PROCESSING.has(processingState)) return 'processing';
  if (processingState === 'failed') return 'failed';

  if (isScoreDisplayable(employee) && !isRiskScoreSnapshotStale(employee, nowMs)) return 'completed';
  return 'stale';
}

function isScoreDisplayable(employee) {
  const payload = employee?.risk_score_payload;
  const score = Number(payload?.score ?? employee?.risk_score);
  if (payload && typeof payload === 'object' && payload.scoreReady === false) return false;
  return Number.isFinite(score);
}

/**
 * UI-facing freshness label — never treat stale scores as "current".
 * @param {object|null|undefined} employee
 * @param {number} [nowMs]
 * @returns {'fresh'|'stale'|'updating'|'failed'}
 */
function deriveFreshnessUiStatus(employee, nowMs = Date.now()) {
  const status = deriveRiskScoreStatus(employee, nowMs);
  if (status === 'processing') return 'updating';
  if (status === 'failed') return 'failed';
  if (status === 'completed') return 'fresh';
  return 'stale';
}

/**
 * @param {object|null|undefined} employee
 * @param {number} [nowMs]
 */
function computeRiskScoreFreshness(employee, nowMs = Date.now()) {
  const lastUpdatedMs = riskScoreLastUpdatedMs(employee);
  const ageMs = lastUpdatedMs != null ? nowMs - lastUpdatedMs : null;
  const isStale = isRiskScoreSnapshotStale(employee, nowMs);
  const isFresh = isScoreDisplayable(employee) && !isStale;
  const riskScoreStatus = deriveRiskScoreStatus(employee, nowMs);
  const freshnessStatus = deriveFreshnessUiStatus(employee, nowMs);

  return {
    riskScoreStatus,
    freshnessStatus,
    isFresh,
    isStale,
    ageMs: ageMs != null && ageMs >= 0 ? ageMs : null,
    lastUpdatedAt:
      lastUpdatedMs != null ? new Date(lastUpdatedMs).toISOString() : null,
    slaMs: RISK_SCORE_FRESHNESS_SLA_MS,
  };
}

/**
 * Idempotent materialization job key per employee (multi-tenant).
 * @param {number} companyId
 * @param {number} employeeId
 */
function riskScoreJobKey(companyId, employeeId) {
  return `risk-score:${companyId}:${employeeId}`;
}

module.exports = {
  RISK_SCORE_FRESHNESS_SLA_MS,
  riskScoreLastUpdatedMs,
  isRiskScoreSnapshotStale,
  isRiskActivelyProcessingEmployee,
  deriveRiskScoreStatus,
  deriveFreshnessUiStatus,
  computeRiskScoreFreshness,
  riskScoreJobKey,
  isScoreDisplayable,
};
